/**
 * Router configuration for React Router v7
 * This file defines all routes, replacing the route creation in App.tsx
 */

// Components
import SiteConfig from 'components/config/SiteConfig';
import Library from 'components/library/Library';
import Error404 from 'components/navigation/Error404';
import Page from 'components/page/Page';
import ProjectList from 'components/project/ProjectList';
import PublicationList from 'components/publication/PublicationList';
import ModalPage from 'components/static/ModalPage';
import StaticPage from 'components/static/StaticPage';
import Team from 'components/team/Team';
import SetLocale from 'components/user/SetLocale';
import Test from 'test/Test';

// Form components for ModalPage
import AdminMaintenanceMode from 'components/admin/AdminMaintenanceMode';
import UserLoginForm from 'components/user/UserLoginForm';
import UserProfileForm from 'components/user/UserProfileForm';
import UserResetPwdForm from 'components/user/UserResetPwdForm';
import UserSignupForm from 'components/user/UserSignupForm';
import UserVerifyMailForm from 'components/user/UserVerifyMailForm';

// Constants
import { IPage } from 'features/page/pageAPI';
import { SLUGS } from 'helpers/consts';

// Available components
export const componentMap = {
  StaticPage,
  Page,
  ProjectList,
  PublicationList,
  Team,
  ModalPage,
  Library,
  SetLocale,
  Error404,
  SiteConfig,
  Test,
} as const;

// Available form components for ModalPage
export const formComponentMap = {
  AdminMaintenanceMode,
  UserLoginForm,
  UserSignupForm,
  UserResetPwdForm,
  UserVerifyMailForm,
  UserProfileForm,
} as const;

export interface RouteConfig {
  path: string;
  component: keyof typeof componentMap;
  params?: string;
  redirect?: string;
  loader?: boolean;
  // Specific layout values supported by Layout component
  layout?: 'white' | 'fixed' | 'mobile';
  restricted?: boolean;
  // Page specific properties
  slug?: string;
  className?: string;
  // ModalPage specific properties
  formComponent?: keyof typeof formComponentMap;
  formComponentProps?: Record<string, any>;
  backdropSlug?: string;
  navigateOnClose?: string;
  // For other component-specific props
  componentProps?: Record<string, any>;
}

// Route definitions
const routeDefinitions: RouteConfig[] = [
  // Root redirect
  // {
  //   path: '/',
  //   component: 'StaticPage',
  //   layout: 'white',
  //   loader: true, // Will redirect to myprojects or welcome based on login status
  // },
  {
    path: '/',
    component: 'Page',
    layout: 'white',
    slug: SLUGS.FRONT,
  },
  // Specific pages
  {
    path: SLUGS.LIBRARY,
    component: 'Library',
    layout: 'white',
  },
  // Locale setting
  {
    path: SLUGS.EN,
    component: 'SetLocale',
    layout: 'white',
    componentProps: { locale: 'en' },
  },
  {
    path: SLUGS.NL,
    component: 'SetLocale',
    layout: 'white',
    componentProps: { locale: 'nl' },
  },
  // Routes for user authentication and management
  {
    path: SLUGS.LOGIN,
    params: '/:code?',
    component: 'ModalPage',
    layout: 'white',
    formComponent: 'UserLoginForm',
    backdropSlug: SLUGS.FRONT,
    navigateOnClose: '/',
  },
  // {
  //   path: SLUGS.SIGNUP[0],
  //   params: '/:code?',
  //   component: 'ModalPage',
  //   layout: 'white',
  //   formComponent: 'UserSignupForm',
  //   backdropSlug: SLUGS.FRONT,
  //   navigateOnClose: '/',
  // },
  // {
  //   path: SLUGS.SIGNUP[1], // Dutch variant of signup
  //   params: '/:code?',
  //   component: 'ModalPage',
  //   layout: 'white',
  //   formComponent: 'UserSignupForm',
  //   backdropSlug: SLUGS.FRONT,
  //   navigateOnClose: '/',
  // },
  {
    path: SLUGS.RESETPWD,
    params: '/:token?',
    component: 'ModalPage',
    layout: 'white',
    formComponent: 'UserResetPwdForm',
    backdropSlug: SLUGS.FRONT,
    navigateOnClose: '/',
  },
  {
    path: SLUGS.VERIFYMAIL,
    params: '/:token/:regcode?',
    component: 'ModalPage',
    layout: 'white',
    formComponent: 'UserVerifyMailForm',
    backdropSlug: SLUGS.FRONT,
    navigateOnClose: '/',
  },
  {
    path: SLUGS.PROFILE[0],
    params: '/:section?',
    component: 'ModalPage',
    layout: 'white',
    formComponent: 'UserProfileForm',
    formComponentProps: { editing: true },
    backdropSlug: SLUGS.FRONT,
    navigateOnClose: '/',
    restricted: true,
  },
  {
    path: SLUGS.PROFILE[1], // Dutch variant of profile
    params: '/:section?',
    component: 'ModalPage',
    layout: 'white',
    formComponent: 'UserProfileForm',
    formComponentProps: { editing: true },
    backdropSlug: SLUGS.FRONT,
    navigateOnClose: '/',
    restricted: true,
  },
  // Admin pages
  {
    path: SLUGS.SITE_CONFIG,
    component: 'SiteConfig',
    layout: 'white',
    restricted: true,
  },
  {
    path: SLUGS.ADMIN_MAINTENANCE,
    component: 'ModalPage',
    layout: 'white',
    formComponent: 'AdminMaintenanceMode',
    backdropSlug: SLUGS.FRONT,
    navigateOnClose: '/',
    restricted: true,
  },
  // 404 catch-all
  {
    path: '*',
    component: 'Error404',
    layout: 'white',
  },
];

// Generate routes for all SLUGS values not already explicitly defined
const allSlugs = Object.values(SLUGS).flat();
const definedPaths = new Set(
  routeDefinitions.filter((r) => r.path !== '*').map((r) => r.path),
);

const generatedSlugRoutes: RouteConfig[] = allSlugs
  .filter((slug) => typeof slug === 'string' && !definedPaths.has(slug))
  .map((slug) => ({
    path: slug as string,
    component: 'Page' as const,
    layout: 'white' as const,
    slug: slug as string,
  }));

// all static routes: explicit routes + generated slug routes + 404 catch-all
export const staticRoutes: RouteConfig[] = [
  ...routeDefinitions.filter((r) => r.path !== '*'),
  ...generatedSlugRoutes,
  routeDefinitions.find((r) => r.path === '*')!,
];

/**
 * Generate complete routes including database pages
 * Call this at runtime in your router builder where Redux state is available
 */
export const generateRoutesWithPages = (pages: IPage[]): RouteConfig[] => {
  if (!pages) {
    return staticRoutes; // If pages data isn't available, return static routes only
  }
  const pageRoutes: RouteConfig[] = pages.map((page) => ({
    path: page.slug as string,
    component: 'Page' as const,
    layout: 'white' as const,
    slug: page.slug as string,
  }));
  const staticDefinedPaths = new Set(
    staticRoutes.filter((r) => r.path !== '*').map((r) => r.path),
  );
  const pagePaths = new Set(pageRoutes.map((r) => r.path));
  const filteredStaticRoutes = staticRoutes.filter(
    (r) => r.path === '*' || !pagePaths.has(r.path),
  );
  return [
    ...pageRoutes,
    ...filteredStaticRoutes.filter((r) => r.path !== '*'),
    filteredStaticRoutes.find((r) => r.path === '*')!,
  ];
};
